import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiPath } from '@/lib/api';

export interface MarketQuote {
  price: number;
  change: number;
  changePct: string;
  prevClose: number;
}

export interface MarketData {
  goldIntl: MarketQuote | null;     // 国际金价 USD/盎司
  goldDomestic: MarketQuote | null; // 沪金 元/克
  sh: MarketQuote | null;           // 上证综指
  sz: MarketQuote | null;           // 深证成指
  cyb: MarketQuote | null;          // 创业板指
  csi300: MarketQuote | null;       // 沪深300
  updatedAt: string | null;
}

const REFRESH_INTERVAL = 60_000; // 60秒刷新一次
const MARKET_CACHE_KEY = 'finance-assistant:last-market-data';

function hasQuotes(data: MarketData) {
  return Boolean(
    data.goldIntl ||
    data.goldDomestic ||
    data.sh ||
    data.sz ||
    data.cyb ||
    data.csi300,
  );
}

function readCachedMarketData(): MarketData | null {
  if (typeof window === 'undefined') return null;

  try {
    const cached = window.localStorage.getItem(MARKET_CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached) as MarketData;
    return parsed && typeof parsed === 'object' && hasQuotes(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function cacheMarketData(data: MarketData) {
  try {
    window.localStorage.setItem(MARKET_CACHE_KEY, JSON.stringify(data));
  } catch {
    // A full or unavailable browser cache should not interrupt live updates.
  }
}

export function useMarketData() {
  const [data, setData] = useState<MarketData | null>(readCachedMarketData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestData = useRef(data);

  const fetchData = useCallback(async () => {
    try {
      const endpoint = getApiPath('market');
      const res = await fetch(endpoint, { cache: 'no-store' });
      if (!res.ok) {
        console.warn('[market] data request failed', {
          endpoint,
          status: res.status,
        });
        throw new Error(`HTTP ${res.status}`);
      }
      const json: MarketData = await res.json();
      if (!hasQuotes(json)) {
        console.warn('[market] data request returned no usable quotes', {
          endpoint,
        });
        throw new Error('Empty market response');
      }
      latestData.current = json;
      setData(json);
      cacheMarketData(json);
      setError(null);
    } catch (e) {
      const hasRecentData = latestData.current !== null;
      setError(
        hasRecentData
          ? '实时行情暂时不可用，页面保留最近一次有效行情。'
          : '实时行情暂时不可用，稍后可点击刷新重试。',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}
